import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import {
  COLLECTIONS,
  newTicketDoc,
  splitPath,
  ticketDueDatePatch,
  ticketMovePatch,
  type NewTicketInput,
} from '@elka/firebase';
import type { Ticket, TicketStatus } from '@elka/types';
import { useAuth } from '../auth/hooks/useAuth';
import { ticketUrl } from './tickets';

/** Writes to tasks-app tickets from the CRM, through the same builders the tasks app uses. */
export function useTicketActions() {
  const { user } = useAuth();

  const actorName = () => user?.displayName || user?.email || 'Unknown';
  const ticketRef = (id: string) => doc(db, ...splitPath(COLLECTIONS.tasks.tickets), id);

  /** Lands in "לביצוע" when someone is assigned, otherwise in the backlog. Returns the id. */
  const createTicket = async (input: NewTicketInput, status?: TicketStatus) => {
    if (!user) throw new Error('לא מחובר');
    const docRef = await addDoc(
      collection(db, ...splitPath(COLLECTIONS.tasks.tickets)),
      newTicketDoc(
        input,
        { uid: user.uid, name: actorName() },
        status ?? (input.assigneeId ? 'todo' : 'backlog')
      )
    );
    return docRef.id;
  };

  const completeTicket = async (ticket: Ticket) => {
    if (!user) throw new Error('לא מחובר');
    // ponytail: a recurring ticket's next occurrence is created only by the tasks app, so
    // completing one opens it there instead of writing a move that would end the series.
    if (ticket.repeat) {
      window.open(ticketUrl(ticket.id), '_blank', 'noopener');
      return;
    }
    await updateDoc(ticketRef(ticket.id), ticketMovePatch('done', actorName()));
  };

  const reopenTicket = async (ticket: Ticket) => {
    if (!user) throw new Error('לא מחובר');
    await updateDoc(ticketRef(ticket.id), ticketMovePatch('todo', actorName()));
  };

  const setDueDate = async (ticket: Ticket, ymd: string) => {
    if (!user) throw new Error('לא מחובר');
    await updateDoc(ticketRef(ticket.id), ticketDueDatePatch(ticket, ymd, actorName()));
  };

  return { createTicket, completeTicket, reopenTicket, setDueDate };
}
